import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { SnackBarService } from '@shared/snack-bar/services/snack-bar.service';
import { getErrorMessage } from '@core/error/errors.model';
import { ErrorStateService } from '@core/error/error-state-store.service';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorMessageHandlerService {
  private readonly snackBarService = inject(SnackBarService);
  private readonly errorStateService = inject(ErrorStateService);
  /**
   * Handles general HTTP errors.
   *
   * This method is invoked by the HTTP interceptor and is executed for **all HTTP requests**,
   * including requests that already define their own `catchError` operators.
   *
   * Purpose:
   * - Centralized logging and monitoring of HTTP errors like
   *    - offline app
   *    - network issues
   *    - CORS errors
   *    - server not found
   *
   * Note:
   * - This method does **not** prevent downstream `catchError` operators from running.
   */
  handleGeneralHttpErrorMessage(error: HttpErrorResponse) {
    let errorMessage = '';
    if (error.error instanceof ProgressEvent || !navigator.onLine) {
      errorMessage = 'messages.noInternetConnection';
      console.error(error.message);
      this.snackBarService.error(errorMessage);
    }
    if (error instanceof HttpErrorResponse) {
      if (error.url?.includes('AttachedFiles')) {
        return;
      }
      switch (error.status) {
        case HttpStatusCode.BadRequest:
          break;
        case HttpStatusCode.Forbidden:
          errorMessage = 'messages.operationNotAllowed';
          break;
        case HttpStatusCode.MethodNotAllowed:
          errorMessage = 'messages.notAllowed';
          break;
        case HttpStatusCode.TooManyRequests:
          errorMessage = 'messages.too-many-requests';
          break;
        case HttpStatusCode.InternalServerError:
          break;
        case HttpStatusCode.HttpVersionNotSupported: {
          if (!this.errorStateService.$errorMessage()) {
            errorMessage = error.error.reason;
          }

          break;
        }

        case HttpStatusCode.ServiceUnavailable:
          break;
        default:
          break;
      }
      console.error(error.error);
      this._showErrorMessage(errorMessage);
    }
  }

  /**
   * Handles fallback (unhandled) HTTP errors.
   *
   * This method is invoked by the global error handler and is executed **only for requests
   * that do NOT define a `catchError` operator**.
   *
   * Purpose:
   * - Final recovery mechanism for unhandled HTTP errors
   * - Prevents silent failures when no local error handling is provided
   *
   * Important:
   * - For requests that include `catchError`, the global error handler will **not** be triggered.
   * - This method acts as a safety net, not a replacement for request-level error handling.
   */
  handleFallbackError(error: HttpErrorResponse) {
    let errorMessage: string | string[] | null = '';
    switch (error.status) {
      case HttpStatusCode.BadRequest:
        errorMessage = getErrorMessage(error);
        break;
      case HttpStatusCode.InternalServerError:
        errorMessage = getErrorMessage(error);
        break;
      case HttpStatusCode.ServiceUnavailable:
        errorMessage = getErrorMessage(error);
        break;
      default:
        break;
    }
    console.error(error.error);
    this._showErrorMessage(errorMessage);
  }

  private _showErrorMessage(message?: string | string[] | null) {
    if (!!message) {
      // Add set time out to make redirection be stable before showing the message
      // in the case of redirecting to another page after error the snackbar message will show for a seconds only
      // adding setTimeout will schedule the message to show after the redirection is done
      setTimeout(() => {
        this.snackBarService.error(message);
      });
    }
  }
}
